import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PageWrapper } from '@/layout/components/PageWrapper.tsx';
import { PageHeader } from '@/layout/components/PageHeader.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { useAnalyzeAudioMutation, type AudioAnalysisData, type AudioStream, type ConvertedFile, type ConversionDebug } from '@/store/audioAnalyzerApi.ts';

const MAX_FILE_SIZE_MB = 50;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(seconds: string | null): string {
  if (!seconds) return '—';
  const s = parseFloat(seconds);
  if (isNaN(s)) return seconds;
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  return m > 0 ? `${m}:${String(Math.floor(s % 60)).padStart(2, '0')} (${s.toFixed(2)}s)` : `${sec}s`;
}

function StatusBadge({ ok, labelOk, labelFail }: { ok: boolean; labelOk: string; labelFail: string }) {
  return ok
    ? <Badge className="bg-green-100 text-green-800 border border-green-300">{labelOk}</Badge>
    : <Badge className="bg-red-100 text-red-800 border border-red-300">{labelFail}</Badge>;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="grid grid-cols-2 gap-2 py-1 border-b border-gray-100 last:border-0 text-sm">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="text-gray-900 break-all">{value}</span>
    </div>
  );
}

function StreamCard({ stream }: { stream: AudioStream }) {
  const isAudio = stream.codecType === 'audio';
  return (
    <Card className={isAudio ? 'border-blue-200 bg-blue-50/40' : ''}>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-semibold text-gray-700">
          Stream #{stream.index} — {stream.codecType || '?'}
          {isAudio && <Badge className="mr-2 bg-blue-100 text-blue-800 border border-blue-200 text-xs">Audio</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-0">
        <Row label="Codec" value={stream.codec} />
        <Row label="Codec (ארוך)" value={stream.codecLongName} />
        <Row label="Profile" value={stream.profile} />
        <Row label="Sample Rate" value={stream.sampleRate ? `${stream.sampleRate} Hz` : null} />
        <Row label="Channels" value={stream.channels} />
        <Row label="Channel Layout" value={stream.channelLayout} />
        <Row label="Sample Format" value={stream.sampleFormat} />
        <Row label="Bits Per Sample" value={stream.bitsPerSample || null} />
        <Row label="Bitrate" value={stream.bitrate ? `${Math.round(parseInt(stream.bitrate) / 1000)} kbps` : null} />
        <Row label="Duration" value={formatDuration(stream.duration)} />
        <Row label="Frame Rate" value={stream.frameRate} />
      </CardContent>
    </Card>
  );
}

function ConversionDebugCard({ debug }: { debug: ConversionDebug }) {
  const commandText = debug.command && debug.command.length > 0 ? debug.command.join(' ') : null;
  return (
    <Card className={debug.error ? 'border-red-200 bg-red-50' : 'border-gray-200'}>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-base">דיבוג המרה לפורמט מרכזיה</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-2">
        <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          <strong>ענף המרה:</strong> {debug.branchUsed}
        </p>
        <Row label="ערוצים במקור" value={debug.sourceChannels} />
        <Row label="קצב דגימה במקור" value={debug.sourceSampleRate ? `${debug.sourceSampleRate} Hz` : null} />
        {debug.sourceProbeOk === false && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            ⚠️ soxi נכשל לקרוא את הקובץ המקורי — הערוצים/קצב הדגימה למעלה הם ערכי ברירת מחדל ולא בהכרח משקפים את הקובץ בפועל.
            {debug.sourceProbeError && <><br /><code className="text-xs">{debug.sourceProbeError}</code></>}
          </p>
        )}
        {commandText && (
          <div className="text-sm">
            <span className="text-gray-500 font-medium block mb-1">פקודה שהורצה</span>
            <code className="block bg-gray-900 text-green-300 rounded px-3 py-2 text-xs break-all">{commandText}</code>
          </div>
        )}
        {debug.error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            ❌ {debug.error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ConvertedFileCard({ converted }: { converted: ConvertedFile }) {
  function handleDownload() {
    if (!converted.base64 || !converted.filename) return;
    const binary = atob(converted.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: converted.mimeType || 'audio/wav' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = converted.filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (converted.error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">המרה לפורמט מרכזיה</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <p className="text-sm text-red-700">שגיאה בהמרה: {converted.error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/40">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-base flex items-center gap-2">
          המרה לפורמט מרכזיה
          <Badge className="bg-green-100 text-green-800 border border-green-300 text-xs">
            {converted.mimeType || 'audio/wav'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        <Row label="שם קובץ" value={converted.filename} />
        <Row label="גודל" value={converted.size ? formatBytes(converted.size) : null} />
        <Button onClick={handleDownload} variant="outline" size="sm" className="w-full">
          ⬇ הורד קובץ מומר
        </Button>
      </CardContent>
    </Card>
  );
}

function AnalysisResults({ data }: { data: AudioAnalysisData }) {
  const { validation } = data;
  const audioStreams = data.streams.filter((s) => s.codecType === 'audio');
  const otherStreams = data.streams.filter((s) => s.codecType !== 'audio');

  const allValidationOk =
    validation.extensionMatchesContainer &&
    validation.detectedMimeMatchesContainer &&
    validation.hasAudioStream &&
    validation.isValid;

  return (
    <div className="space-y-4">
      {/* Validation Summary */}
      <Card className={allValidationOk ? 'border-green-300' : 'border-amber-300'}>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">תוצאת אימות</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <StatusBadge
              ok={validation.isValid}
              labelOk="קובץ תקין"
              labelFail="קובץ לא תקין"
            />
            <StatusBadge
              ok={validation.hasAudioStream}
              labelOk="יש Audio Stream"
              labelFail="אין Audio Stream"
            />
            <StatusBadge
              ok={validation.extensionMatchesContainer}
              labelOk="סיומת תואמת Container"
              labelFail="סיומת לא תואמת Container"
            />
            <StatusBadge
              ok={validation.detectedMimeMatchesContainer}
              labelOk="MIME תואם Container"
              labelFail="MIME לא תואם Container"
            />
          </div>

          {!validation.extensionMatchesContainer && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
              ⚠️ הסיומת <strong>.{data.extension}</strong> לא תואמת את ה-Container שזוהה:{' '}
              <strong>{data.container}</strong>
            </p>
          )}
          {!validation.detectedMimeMatchesContainer && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚠️ ה-MIME שזוהה לפי תוכן (<strong>{data.detectedMimeType}</strong>) לא תואם את ה-Container:{' '}
              <strong>{data.container}</strong>
            </p>
          )}
          {!validation.hasAudioStream && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              ❌ הקובץ אינו מכיל Audio Stream — לא ניתן להמיר למרכזיה.
            </p>
          )}
        </CardContent>
      </Card>

      {/* File Info */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">מידע כללי על הקובץ</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-0">
          <Row label="שם קובץ" value={data.filename} />
          <Row label="סיומת" value={data.extension ? `.${data.extension}` : '(ללא סיומת)'} />
          <Row label="גודל" value={formatBytes(data.fileSize)} />
          <Row label="MIME (לפי תוכן)" value={data.detectedMimeType} />
          <Row label="Container" value={data.container} />
          <Row label="Container (ארוך)" value={data.containerLongName} />
        </CardContent>
      </Card>

      {/* Format */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">Format</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-0">
          <Row label="Duration" value={formatDuration(data.format.duration)} />
          <Row label="Bitrate" value={data.format.bitrate ? `${Math.round(parseInt(data.format.bitrate) / 1000)} kbps` : null} />
          <Row label="Size" value={data.format.size ? formatBytes(parseInt(data.format.size)) : null} />
          <Row label="Start Time" value={data.format.startTime} />
          <Row label="Probe Score" value={data.format.probeScore} />
        </CardContent>
      </Card>

      {/* Audio Streams */}
      {audioStreams.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Audio Streams ({audioStreams.length})</h3>
          {audioStreams.map((s) => (
            <StreamCard key={s.index} stream={s} />
          ))}
        </div>
      )}

      {/* Other Streams */}
      {otherStreams.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-500">Streams נוספים ({otherStreams.length})</h3>
          {otherStreams.map((s) => (
            <StreamCard key={s.index} stream={s} />
          ))}
        </div>
      )}

      {/* Converted file */}
      {data.convertedFile && <ConvertedFileCard converted={data.convertedFile} />}
      {data.conversionDebug && <ConversionDebugCard debug={data.conversionDebug} />}
    </div>
  );
}

export default function AudioAnalyzerPage() {
  const [analyzeAudio, { isLoading }] = useAnalyzeAudioMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<AudioAnalysisData | null>(null);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File | null) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`גודל הקובץ חורג מהמגבלה של ${MAX_FILE_SIZE_MB}MB`);
      return;
    }
    setSelectedFile(file);
    setResult(null);
    setAnalyzeError(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0] ?? null;
      handleFile(file);
    },
    [handleFile],
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  async function handleAnalyze() {
    if (!selectedFile) {
      toast.error('יש לבחור קובץ תחילה');
      return;
    }
    setResult(null);
    setAnalyzeError(null);
    try {
      const res = await analyzeAudio({ file: selectedFile }).unwrap();
      if (!res.success) {
        setAnalyzeError(res.error ?? 'שגיאה לא ידועה בניתוח הקובץ');
        toast.error('ניתוח הקובץ נכשל');
        return;
      }
      setResult(res.data);
      toast.success('הניתוח הושלם בהצלחה');
    } catch (err: unknown) {
      const msg =
        (err as { data?: { msg?: string; error?: string } })?.data?.msg ||
        (err as { data?: { msg?: string; error?: string } })?.data?.error ||
        'אירעה שגיאה בתהליך הניתוח';
      setAnalyzeError(msg);
      toast.error(msg);
    }
  }

  return (
    <PageWrapper>
      <PageHeader title="בדיקת תקינות קבצי אודיו" />

      <div className="max-w-3xl w-full space-y-6">
        {/* Upload area */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-base">בחירת קובץ</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            {/* Drop zone */}
            <div
              role="button"
              tabIndex={0}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/30'}`}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="text-4xl mb-2">🎵</div>
              {selectedFile ? (
                <div className="space-y-1">
                  <p className="font-medium text-gray-800">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">{formatBytes(selectedFile.size)}</p>
                  <p className="text-xs text-blue-500 mt-1">לחץ כאן או גרור קובץ חדש להחלפה</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-gray-600 font-medium">גרור קובץ לכאן, או לחץ לבחירה</p>
                  <p className="text-sm text-gray-400">כל סוגי קבצי האודיו מותרים, עד {MAX_FILE_SIZE_MB}MB</p>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />

            <Button
              onClick={handleAnalyze}
              disabled={!selectedFile || isLoading}
              className="w-full"
            >
              {isLoading ? 'מנתח...' : 'נתח קובץ'}
            </Button>
          </CardContent>
        </Card>

        {/* Error */}
        {analyzeError && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="px-4 py-4">
              <p className="text-red-700 font-medium">שגיאה בניתוח:</p>
              <p className="text-red-600 text-sm mt-1">{analyzeError}</p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && <AnalysisResults data={result} />}
      </div>
    </PageWrapper>
  );
}
